import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import FileUploadCard from '../components/FileUploadCard';
import { useAppCtx } from '../context/AppCtx';
import type { AnalysisConfig, BatchOut, FileKind, MondayColumnOut, MondayConfigIn, MondayConfigOut } from '../types/api';

// ─── Definições de arquivos ────────────────────────────────────────────────

const ALL_FILES: {
  kind: FileKind; icon: string; label: string; desc: string; required: boolean;
}[] = [
  { kind: 'vendas',            icon: '📈', label: 'Curva de Vendas 90 dias',         desc: 'Lexos · Sku, Nome, Marca, Estoque, Vendidas, ABC',    required: true },
  { kind: 'estoque',           icon: '📦', label: 'Estoque Atual',                    desc: 'Lexos · Sku, Qtd, Custo, Preço, Plataformas',         required: true },
  { kind: 'margem',            icon: '📊', label: 'Planilha de Margem (BI)',          desc: 'BI · "Sku - Descrição" na coluna B',                  required: true },
  { kind: 'pedidos',           icon: '🧾', label: 'Pedidos de Compra em Aberto',      desc: 'Protheus · trânsito p/ disponibilidade',              required: false },
  { kind: 'regras',            icon: '📕', label: 'Regras Comerciais por Fornecedor', desc: 'Opcional · prazos, mínimos, lead time',               required: false },
  { kind: 'full_amazon',       icon: '🟧', label: 'Estoque FULL Amazon',              desc: 'Amazon FBA · sku + available + inbound',              required: false },
  { kind: 'full_mercadolivre', icon: '🟨', label: 'Estoque FULL Mercado Livre',       desc: 'Meli FULL · SKU + Estoque no depósito',               required: false },
  { kind: 'full_shopee',       icon: '🟪', label: 'Estoque FULL Shopee',              desc: 'Shopee FBS · Seller SKU + Sellable + Pending',        required: false },
  { kind: 'full_magalu',       icon: '🟦', label: 'Estoque FULL Magalu',              desc: 'Magalu · Código ref + À venda + Em trânsito',         required: false },
];

const OPTIONAL_FILE_KINDS = new Set<FileKind>([
  'pedidos',
  'regras',
  'full_amazon',
  'full_mercadolivre',
  'full_shopee',
  'full_magalu',
]);

// ─── Defaults de config ────────────────────────────────────────────────────

const DEFAULT_CFG: AnalysisConfig = {
  sales_window_days: 90,
  horizon_days: 45,
  excess_cover_days: 90,
  min_margin: 10,
  target_margin: 15,
  block_reduction: 30,
  prazo_padrao: '28/56',
  lead_time_dias: 7,
};

// ─── Monday Panel ──────────────────────────────────────────────────────────

function MondayPanel() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery<MondayConfigOut | null>({
    queryKey: ['monday-config'],
    queryFn: () => api.get<MondayConfigOut | null>('/monday/config').then(r => r.data),
  });
  const [token, setToken] = useState('');
  const [boardId, setBoardId] = useState('');
  const [autoSync, setAutoSync] = useState(false);
  const [colEtiquetas, setColEtiquetas] = useState('');
  const [colArquivoPdf, setColArquivoPdf] = useState('');
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const conectado = !!cfg?.board_id;
  const jaConfigurado = conectado;
  const columnsQuery = useQuery<MondayColumnOut[]>({
    queryKey: ['monday-config-columns', cfg?.board_id],
    queryFn: () => api.get<MondayColumnOut[]>('/monday/config/columns').then(r => r.data),
    enabled: open && conectado,
  });
  const mondayColumns = columnsQuery.data ?? [];
  const statusColumns = mondayColumns.filter(c => {
    const type = c.type.toLowerCase();
    const title = c.title.toLowerCase();
    return type === 'status' || type === 'color' || /status|etiqueta|label|tag|agendamento/.test(title);
  });
  const otherColumns = mondayColumns.filter(c => !statusColumns.some(s => s.id === c.id));
  const currentColumn = mondayColumns.find(c => c.id === colEtiquetas);
  const fileColumns = mondayColumns.filter(c => c.type.toLowerCase() === 'file');
  const currentFileColumn = mondayColumns.find(c => c.id === colArquivoPdf);

  const saveMut = useMutation({
    mutationFn: (payload: MondayConfigIn) => api.put<MondayConfigOut>('/monday/config', payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['monday-config'] }); setOpen(false); setErr(null); },
    onError: (e: any) => setErr(e?.response?.data?.detail || String(e)),
  });

  function handleOpen() {
    setOpen(p => !p);
    if (!open && cfg) {
      setToken('');
      setBoardId(cfg.board_id);
      setAutoSync(cfg.auto_sync);
      setColEtiquetas(cfg.column_map?.etiquetas ?? '');
      setColArquivoPdf(cfg.column_map?.arquivo_pdf ?? '');
    }
  }

  function handleSave() {
    const column_map: Record<string, string> = { ...(cfg?.column_map ?? {}) };
    if (colEtiquetas) column_map.etiquetas = colEtiquetas; else delete column_map.etiquetas;
    if (colArquivoPdf) column_map.arquivo_pdf = colArquivoPdf; else delete column_map.arquivo_pdf;
    saveMut.mutate({
      api_token: token,
      board_id: boardId,
      auto_sync: autoSync,
      column_map: Object.keys(column_map).length ? column_map : undefined,
    });
  }

  return (
    <div className="card p-5" style={{ background: 'linear-gradient(135deg,rgba(99,102,241,.08) 0%,rgba(99,102,241,.02) 100%)', borderColor: 'rgba(99,102,241,.35)' }}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="text-4xl">🟦</div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-extrabold text-base flex items-center gap-2 flex-wrap">
            Integração Monday · AGENDAMENTOS MBC 2026
            {conectado
              ? <span className="badge bg-emerald-500/20 text-emerald-300">conectado</span>
              : <span className="badge bg-amber-500/20 text-amber-300">desconectado</span>}
          </h3>
          <p className="text-xs text-mbc-mute mt-1">
            {conectado ? `Board ${cfg?.board_id}` : 'Configure o token para sincronizar pedidos aprovados com o Monday.com'}
          </p>
          <button type="button" onClick={handleOpen} className="mt-2 text-xs text-mbc-orange hover:underline">
            {open ? '▲ fechar' : '▼ configurar'}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm items-start">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-mbc-mute">
              API Token {jaConfigurado ? <span className="text-emerald-400">(salvo — preencha só para trocar)</span> : '*'}
            </span>
            <input
              type="password" value={token} onChange={e => setToken(e.target.value)}
              placeholder={jaConfigurado ? '••••••••  (deixe vazio para manter)' : 'eyJhbGciOiJIUzI1NiJ9…'}
              className="bg-mbc-panel border border-mbc-line rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-mbc-orange"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-mbc-mute">Board ID *</span>
            <input
              type="text" value={boardId} onChange={e => setBoardId(e.target.value)}
              placeholder="1234567890"
              className="bg-mbc-panel border border-mbc-line rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-mbc-orange"
            />
          </label>
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="text-xs text-mbc-mute">
              Coluna de etiqueta/status <span className="text-mbc-orange">(→ marca "Full SP" ou "Sem agendamento")</span>
            </span>
            {conectado && mondayColumns.length > 0 ? (
              <select
                value={colEtiquetas}
                onChange={e => setColEtiquetas(e.target.value)}
                className="bg-mbc-panel border border-mbc-line rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-mbc-orange"
              >
                <option value="">Auto-detectar coluna de status/etiqueta</option>
                {statusColumns.length > 0 && (
                  <optgroup label="Recomendadas">
                    {statusColumns.map(c => (
                      <option key={c.id} value={c.id}>{c.title} ({c.id} · {c.type})</option>
                    ))}
                  </optgroup>
                )}
                {otherColumns.length > 0 && (
                  <optgroup label="Outras colunas">
                    {otherColumns.map(c => (
                      <option key={c.id} value={c.id}>{c.title} ({c.id} · {c.type})</option>
                    ))}
                  </optgroup>
                )}
              </select>
            ) : (
              <input
                type="text" value={colEtiquetas} onChange={e => setColEtiquetas(e.target.value)}
                placeholder="Configure/salve o board para listar as colunas ou informe o Column ID"
                className="bg-mbc-panel border border-mbc-line rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-mbc-orange"
              />
            )}
            {conectado && columnsQuery.isLoading && <span className="text-xs text-mbc-mute">Carregando colunas do Monday...</span>}
            {conectado && columnsQuery.isError && (
              <span className="text-xs text-amber-300">
                Não foi possível listar colunas. Você ainda pode informar o Column ID manualmente.
              </span>
            )}
            {currentColumn && (
              <span className="text-xs text-emerald-400">
                Selecionada: {currentColumn.title} ({currentColumn.id})
              </span>
            )}
          </label>
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="text-xs text-mbc-mute">
              Coluna do PDF <span className="text-mbc-orange">(opcional · tipo file/anexo no Monday)</span>
            </span>
            {conectado && mondayColumns.length > 0 ? (
              <select
                value={colArquivoPdf}
                onChange={e => setColArquivoPdf(e.target.value)}
                className="bg-mbc-panel border border-mbc-line rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-mbc-orange"
              >
                <option value="">Auto-detectar primeira coluna de arquivo</option>
                {fileColumns.map(c => (
                  <option key={c.id} value={c.id}>{c.title} ({c.id})</option>
                ))}
              </select>
            ) : (
              <input
                type="text" value={colArquivoPdf} onChange={e => setColArquivoPdf(e.target.value)}
                placeholder="Configure/salve o board para listar as colunas ou informe o Column ID"
                className="bg-mbc-panel border border-mbc-line rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-mbc-orange"
              />
            )}
            {conectado && mondayColumns.length > 0 && fileColumns.length === 0 && (
              <span className="text-xs text-amber-300">
                Sem coluna file no board. O pedido ainda será enviado; PDFs só serão anexados quando houver PDF salvo e coluna configurada.
              </span>
            )}
            {currentFileColumn && (
              <span className="text-xs text-emerald-400">
                Selecionada: {currentFileColumn.title} ({currentFileColumn.id})
              </span>
            )}
          </label>
          {err && <p className="text-rose-300 text-xs col-span-full">{err}</p>}
          <div className="flex flex-wrap items-center gap-2 col-span-full">
            <label className="flex items-center gap-2 text-xs text-mbc-mute cursor-pointer mr-auto">
              <button
                type="button" onClick={() => setAutoSync(p => !p)}
                className={`w-10 h-5 rounded-full transition-colors relative ${autoSync ? 'bg-mbc-orange' : 'bg-mbc-line'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoSync ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
              Auto-sync ao aprovar pedidos
            </label>
            <button
              disabled={(!token && !jaConfigurado) || !boardId || saveMut.isPending}
              onClick={handleSave}
              className="bg-mbc-orange hover:bg-mbc-orangel disabled:opacity-40 text-white text-xs font-bold px-4 py-2 rounded-lg"
            >
              {saveMut.isPending ? 'Salvando…' : 'Salvar e validar'}
            </button>
            <button onClick={() => setOpen(false)} className="text-mbc-mute hover:text-white text-xs px-3">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Página ────────────────────────────────────────────────────────────────

export default function Upload() {
  const nav = useNavigate();
  const ctx = useAppCtx();
  const [batch, setBatch] = useState<BatchOut | null>(null);
  const [busy, setBusy] = useState(false);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cfg, setCfg] = useState<AnalysisConfig>({ ...DEFAULT_CFG, horizon_days: ctx.horizon });
  const [importLog, setImportLog] = useState<string | null>(null);

  useEffect(() => {
    api.get<BatchOut | null>('/upload/batch/current').then(r => {
      if (r.data) setBatch(r.data);
      else ensureBatch();
      if (r.data?.processed_at) ctx.setDataStamp(new Date(r.data.processed_at));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync topbar horizon → local cfg
  useEffect(() => {
    setCfg(prev => ({ ...prev, horizon_days: ctx.horizon }));
  }, [ctx.horizon]);

  async function ensureBatch(kind?: FileKind) {
    if (batch && (!batch.processed || (kind && OPTIONAL_FILE_KINDS.has(kind)))) return batch;
    const r = await api.post<BatchOut>('/upload/batch');
    setBatch(r.data);
    return r.data;
  }

  async function onUpload(kind: FileKind, file: File) {
    const b = await ensureBatch(kind);
    const fd = new FormData();
    fd.append('file', file);
    const r = await api.post<BatchOut>(`/upload/batch/${b.id}/file/${kind}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setBatch(r.data);
    if (b.processed && OPTIONAL_FILE_KINDS.has(kind)) {
      ctx.setDataStamp(new Date());
      setImportLog('✓ Arquivo opcional atualizado e análise reprocessada automaticamente. Já refletido no Comprador.');
    }
  }

  async function processar() {
    if (!batch) return;
    setBusy(true);
    setErr(null);
    setImportLog('⏳ Salvando dados…');
    try {
      const payload = {
        ...cfg,
        min_margin: cfg.min_margin / 100,
        target_margin: cfg.target_margin / 100,
        block_reduction: cfg.block_reduction / 100,
      };
      // Quando os arquivos ainda existem, reprocessa do zero para atualizar
      // campos persistidos vindos das planilhas, como custo da mercadoria.
      const endpoint = !batch.processed || batch.has_all_files
        ? `/upload/batch/${batch.id}/process`
        : `/upload/batch/${batch.id}/recalc`;
      const r = await api.post<BatchOut>(endpoint, payload);
      setBatch(r.data);
      const stamp = new Date();
      ctx.setDataStamp(stamp);
      ctx.setHorizon(cfg.horizon_days);
      setImportLog(`✓ Análise concluída · ${r.data.items_count.toLocaleString('pt-BR')} SKUs processados`);
      nav('/dashboard');
    } catch (ex: any) {
      const msg = ex?.response?.data?.detail || String(ex);
      setErr(msg);
      setImportLog(null);
    } finally {
      setBusy(false);
    }
  }

  async function salvarSnapshot() {
    if (!batch) return;
    setSnapshotBusy(true);
    try {
      const r = await api.post<BatchOut>(`/upload/batch/${batch.id}/snapshot`);
      setBatch(r.data);
    } catch (ex: any) {
      setErr(ex?.response?.data?.detail || String(ex));
    } finally {
      setSnapshotBusy(false);
    }
  }

  async function limparDados() {
    const r = await api.post<BatchOut>('/upload/batch');
    setBatch(r.data);
    ctx.setDataStamp(null);
    setImportLog(null);
  }

  const set = <K extends keyof AnalysisConfig>(k: K, v: AnalysisConfig[K]) =>
    setCfg(prev => ({ ...prev, [k]: v }));

  const missingRequired = [
    !batch?.has_vendas && 'vendas',
    !batch?.has_margem && 'margem',
    !batch?.has_estoque && 'estoque',
  ].filter(Boolean);

  const canProcess = !!(batch?.has_vendas && batch?.has_margem && batch?.has_estoque);

  function isUploaded(kind: FileKind): boolean {
    if (!batch) return false;
    const key = `has_${kind}` as keyof BatchOut;
    return Boolean(batch[key]);
  }

  return (
    <div className="space-y-6 fade-in">

      {/* ─── Card principal: upload de arquivos ─── */}
      <div className="card p-6 glow">
        <h2 className="text-white font-bold text-lg flex items-center gap-2">📥 Importação de Arquivos</h2>
        <p className="text-sm text-mbc-mute mt-1">
          Importe os arquivos do Lexos / BI. Os dois últimos são opcionais nesta versão — baixe os modelos se ainda não tiver.
        </p>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
          {ALL_FILES.map(f => (
            <FileUploadCard
              key={f.kind}
              kind={f.kind}
              icon={f.icon}
              label={f.label}
              description={f.desc}
              required={f.required}
              uploaded={isUploaded(f.kind)}
              onUpload={onUpload}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <button
            disabled={!canProcess || busy}
            onClick={processar}
            className="pill-btn bg-mbc-orange hover:bg-mbc-orangel text-white font-bold px-6 py-2.5 rounded-xl shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? '⏳ processando…' : batch?.processed ? '⚙️ Reprocessar & Atualizar' : '⚙️ Processar & Analisar'}
          </button>
          <button
            disabled={!batch || snapshotBusy}
            onClick={salvarSnapshot}
            className="pill-btn bg-mbc-blue hover:bg-mbc-bluel text-white font-semibold px-5 py-2.5 rounded-xl disabled:opacity-40"
          >
            {snapshotBusy ? '⏳…' : batch?.snapshot_at ? '✓ Snapshot salvo' : '💾 Salvar snapshot do dia'}
          </button>
          <span className="text-xs text-mbc-mute">
            {canProcess
              ? 'Pronto para processar.'
              : `Faltam arquivos obrigatórios${missingRequired.length ? ': ' + missingRequired.join(', ') : ''}.`}
          </span>
          <button
            type="button"
            onClick={limparDados}
            title="Apaga os arquivos vendas/margem/estoque salvos. Pedidos, snapshots e workflow são preservados."
            className="ml-auto text-xs text-rose-300 hover:text-rose-200 underline"
          >
            🗑 limpar dados salvos
          </button>
        </div>
      </div>

      {/* ─── Config panels ─── */}
      <div className="grid md:grid-cols-3 gap-4">

        {/* ① Projeção & Ruptura */}
        <div className="card p-5">
          <div className="text-mbc-orange font-bold text-sm mb-3">① Projeção &amp; Ruptura</div>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-mbc-mute block mb-1">Janela de vendas importada</label>
              <select
                value={cfg.sales_window_days}
                onChange={e => set('sales_window_days', +e.target.value)}
                className="w-full bg-mbc-panel2 border border-mbc-line rounded-lg px-2 py-1.5 text-mbc-soft"
              >
                {[30, 60, 90, 120, 180].map(d => <option key={d} value={d}>{d} dias</option>)}
              </select>
            </div>
            <div>
              <label className="text-mbc-mute block mb-1">Horizonte de projeção</label>
              <select
                value={cfg.horizon_days}
                onChange={e => { set('horizon_days', +e.target.value); ctx.setHorizon(+e.target.value); }}
                className="w-full bg-mbc-panel2 border border-mbc-line rounded-lg px-2 py-1.5 text-mbc-soft"
              >
                {[15, 30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} dias</option>)}
              </select>
            </div>
            <div>
              <label className="text-mbc-mute block mb-1">Cobertura considerada excesso</label>
              <div className="flex items-center gap-1">
                <input
                  type="number" min={30} step={10} value={cfg.excess_cover_days}
                  onChange={e => set('excess_cover_days', +e.target.value)}
                  className="flex-1 bg-mbc-panel2 border border-mbc-line rounded-lg px-2 py-1.5 text-mbc-soft"
                />
                <span className="text-mbc-mute text-[10px]">dias acima → excesso</span>
              </div>
            </div>
            <div className="text-[10px] text-mbc-mute leading-snug pt-1 border-t border-mbc-line">
              Consumo diário = vendas ÷ <strong className="text-mbc-soft">{cfg.sales_window_days}</strong>d.{' '}
              Projeção <strong className="text-mbc-soft">{cfg.horizon_days}</strong>d.{' '}
              Ruptura = max(0, consumo − disponível total).
            </div>
          </div>
        </div>

        {/* ② Margem */}
        <div className="card p-5">
          <div className="text-mbc-orange font-bold text-sm mb-3">② Margem (mín. {cfg.min_margin}%)</div>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-mbc-mute block mb-1">Margem mínima aceitável</label>
              <div className="flex items-center gap-1">
                <input
                  type="number" min={0} max={100} step={0.5} value={cfg.min_margin}
                  onChange={e => set('min_margin', +e.target.value)}
                  className="flex-1 bg-mbc-panel2 border border-mbc-line rounded-lg px-2 py-1.5 text-mbc-soft"
                />
                <span className="text-mbc-mute">%</span>
              </div>
            </div>
            <div>
              <label className="text-mbc-mute block mb-1">Margem alvo de negociação</label>
              <div className="flex items-center gap-1">
                <input
                  type="number" min={0} max={100} step={0.5} value={cfg.target_margin}
                  onChange={e => set('target_margin', +e.target.value)}
                  className="flex-1 bg-mbc-panel2 border border-mbc-line rounded-lg px-2 py-1.5 text-mbc-soft"
                />
                <span className="text-mbc-mute">%</span>
              </div>
            </div>
            <div>
              <label className="text-mbc-mute block mb-1">Bloquear se reduzir custo &gt;</label>
              <div className="flex items-center gap-1">
                <input
                  type="number" min={0} max={100} step={1} value={cfg.block_reduction}
                  onChange={e => set('block_reduction', +e.target.value)}
                  className="flex-1 bg-mbc-panel2 border border-mbc-line rounded-lg px-2 py-1.5 text-mbc-soft"
                />
                <span className="text-mbc-mute">%</span>
              </div>
            </div>
            <div className="text-[10px] text-mbc-mute leading-snug pt-1 border-t border-mbc-line">
              Preço-teto = preço × (1 − <strong className="text-mbc-soft">{cfg.min_margin}%</strong>).{' '}
              Negociar = (custo − teto) ÷ custo. Bloqueado se exigir &gt; <strong className="text-mbc-soft">{cfg.block_reduction}%</strong>.
            </div>
          </div>
        </div>

        {/* ③④ Comercial & Financeiro */}
        <div className="card p-5">
          <div className="text-mbc-orange font-bold text-sm mb-3">③④ Comercial &amp; Financeiro</div>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-mbc-mute block mb-1">Prazo padrão de pagamento</label>
              <input
                type="text" value={cfg.prazo_padrao}
                onChange={e => set('prazo_padrao', e.target.value)}
                placeholder="ex: 28/56/84"
                className="w-full bg-mbc-panel2 border border-mbc-line rounded-lg px-2 py-1.5 text-mbc-soft"
              />
            </div>
            <div>
              <label className="text-mbc-mute block mb-1">Lead time padrão (sem cadastro)</label>
              <div className="flex items-center gap-1">
                <input
                  type="number" min={0} max={365} step={1} value={cfg.lead_time_dias}
                  onChange={e => set('lead_time_dias', +e.target.value)}
                  className="flex-1 bg-mbc-panel2 border border-mbc-line rounded-lg px-2 py-1.5 text-mbc-soft"
                />
                <span className="text-mbc-mute">dias</span>
              </div>
            </div>
            <div className="text-[10px] text-mbc-mute leading-snug pt-1 border-t border-mbc-line">
              Workflow de compras, validação de prazo e exposição por vencimento. Usados quando o fornecedor não tem cadastro em "Regras Comerciais".
            </div>
          </div>
        </div>
      </div>

      {/* ─── Monday panel ─── */}
      <MondayPanel />

      {/* ─── Import log ─── */}
      {importLog && (
        <div className="card p-5 text-xs text-mbc-mute">
          <div className="text-emerald-400 font-semibold">{importLog}</div>
          {batch?.snapshot_at && (
            <div className="mt-1">Snapshot salvo em {new Date(batch.snapshot_at).toLocaleString('pt-BR')}</div>
          )}
        </div>
      )}

      {err && <p className="text-rose-300 text-sm">{err}</p>}
    </div>
  );
}
